// T017: Base Effect Class

import type {
  Effect,
  EffectType,
  EffectTrigger,
  EffectContext,
  EffectResult,
  StateChange,
} from '../../types';

/**
 * Abstract base class for all effects.
 * Each effect type should extend this class and implement canActivate/execute.
 */
export abstract class BaseEffect implements Effect {
  abstract readonly type: EffectType;
  abstract readonly trigger: EffectTrigger;
  abstract readonly priority: number;

  abstract canActivate(context: EffectContext): boolean;
  abstract execute(context: EffectContext): EffectResult;

  /**
   * Helper to create a successful result with state changes.
   */
  protected success(stateChanges: StateChange[], triggeredEffects?: EffectType[]): EffectResult {
    return {
      success: true,
      stateChanges,
      triggeredEffects,
    };
  }

  /**
   * Helper to create a failed result.
   */
  protected failure(): EffectResult {
    return {
      success: false,
      stateChanges: [],
    };
  }
}

/**
 * Pierce Effect (貫穿)
 * Ignores defense phase - attack cannot be blocked.
 */
export class PierceEffect extends BaseEffect {
  readonly type = 'pierce' as const;
  readonly trigger = 'onAttackDeclare' as const;
  readonly priority = 10;

  canActivate(context: EffectContext): boolean {
    return context.phase === 'tactics' && context.combatInfo !== undefined;
  }

  execute(context: EffectContext): EffectResult {
    // Pierce modifies combat resolution - marks attack as unblockable
    return this.success([
      {
        type: 'ADD_EFFECT',
        targetId: context.sourceCard.id,
        value: { piercing: true },
      },
    ]);
  }
}

/**
 * Ranged Effect (射程)
 * Can attack back row directly, ignoring front row.
 */
export class RangedEffect extends BaseEffect {
  readonly type = 'ranged' as const;
  readonly trigger = 'onAttackDeclare' as const;
  readonly priority = 10;

  canActivate(context: EffectContext): boolean {
    return context.phase === 'tactics' && context.combatInfo !== undefined;
  }

  execute(context: EffectContext): EffectResult {
    return this.success([
      {
        type: 'ADD_ATTACK_TARGET',
        targetId: context.sourceCard.id,
        value: { canTargetBackRow: true },
      },
    ]);
  }
}

/**
 * Block Effect (格擋)
 * Can intercept attacks on adjacent columns.
 */
export class BlockEffect extends BaseEffect {
  readonly type = 'block' as const;
  readonly trigger = 'onDefend' as const;
  readonly priority = 20;

  canActivate(context: EffectContext): boolean {
    return context.phase === 'defense' && context.combatInfo !== undefined;
  }

  execute(context: EffectContext): EffectResult {
    // Block redirects attack to this unit
    return this.success([
      {
        type: 'ADD_EFFECT',
        targetId: context.sourceCard.id,
        value: { blocking: true },
      },
    ]);
  }
}

/**
 * Cover Effect (掩護)
 * Can protect back row units from attacks.
 */
export class CoverEffect extends BaseEffect {
  readonly type = 'cover' as const;
  readonly trigger = 'onDefend' as const;
  readonly priority = 20;

  canActivate(context: EffectContext): boolean {
    return context.phase === 'defense';
  }

  execute(context: EffectContext): EffectResult {
    return this.success([
      {
        type: 'ADD_EFFECT',
        targetId: context.sourceCard.id,
        value: { covering: true },
      },
    ]);
  }
}

/**
 * Poison Effect (毒)
 * Target goes to Forest instead of Graveyard.
 */
export class PoisonEffect extends BaseEffect {
  readonly type = 'poison' as const;
  readonly trigger = 'onCombatResolve' as const;
  readonly priority = 30;

  canActivate(context: EffectContext): boolean {
    return context.combatInfo !== undefined;
  }

  execute(context: EffectContext): EffectResult {
    if (!context.combatInfo) return this.failure();

    return this.success([
      {
        type: 'ADD_EFFECT',
        targetId: context.combatInfo.defenderId,
        value: { poisoned: true, destination: 'forest' },
      },
    ]);
  }
}

/**
 * Reward Effect (賞)
 * Draw 1 card when this unit is destroyed in combat.
 */
export class RewardEffect extends BaseEffect {
  readonly type = 'reward' as const;
  readonly trigger = 'onDestroy' as const;
  readonly priority = 50;

  canActivate(context: EffectContext): boolean {
    return context.sourceCard.location === 'battlefield';
  }

  execute(context: EffectContext): EffectResult {
    return this.success([
      {
        type: 'ADD_RESOURCE',
        targetId: context.sourceCard.ownerId,
        value: { draw: 1 },
      },
    ]);
  }
}

/**
 * Soul Effect (靈魂)
 * Unit can be revived from graveyard.
 */
export class SoulEffect extends BaseEffect {
  readonly type = 'soul' as const;
  readonly trigger = 'onDestroy' as const;
  readonly priority = 40;

  canActivate(context: EffectContext): boolean {
    // Check if unit has soul charges remaining
    const soulCharges = context.sourceCard.activeEffects?.filter((e) => e === 'soul').length ?? 0;
    return soulCharges > 0;
  }

  execute(context: EffectContext): EffectResult {
    return this.success([
      {
        type: 'ADD_EFFECT',
        targetId: context.sourceCard.id,
        value: { canRevive: true },
      },
    ]);
  }
}

/**
 * Overload Effect (填充)
 * Allows multiple copies of this card in deck.
 */
export class OverloadEffect extends BaseEffect {
  readonly type = 'overload' as const;
  readonly trigger = 'onSummon' as const;
  readonly priority = 0;

  // This effect is passive for deck building - always activatable
  canActivate(): boolean {
    return true;
  }

  execute(): EffectResult {
    // Overload has no runtime effect, only affects deck building
    return this.success([]);
  }
}

/**
 * Slide Effect (滑壘)
 * After combat, move to an adjacent empty slot.
 */
export class SlideEffect extends BaseEffect {
  readonly type = 'slide' as const;
  readonly trigger = 'onCombatResolve' as const;
  readonly priority = 40;

  canActivate(context: EffectContext): boolean {
    return context.combatInfo !== undefined;
  }

  execute(context: EffectContext): EffectResult {
    return this.success([
      {
        type: 'ADD_EFFECT',
        targetId: context.sourceCard.id,
        value: { canSlide: true },
      },
    ]);
  }
}

/**
 * Ambush Effect (伏擊)
 * Can attack immediately after summoning.
 */
export class AmbushEffect extends BaseEffect {
  readonly type = 'ambush' as const;
  readonly trigger = 'onSummon' as const;
  readonly priority = 5;

  canActivate(context: EffectContext): boolean {
    return context.phase === 'summon' || context.phase === 'tactics';
  }

  execute(context: EffectContext): EffectResult {
    return this.success([
      {
        type: 'CHANGE_ORIENTATION',
        targetId: context.sourceCard.id,
        value: 'tilted', // Ready to attack
      },
    ]);
  }
}

/**
 * Electrify Effect (通電)
 * Deal 1 damage to attacking unit when defending.
 */
export class ElectrifyEffect extends BaseEffect {
  readonly type = 'electrify' as const;
  readonly trigger = 'onDefend' as const;
  readonly priority = 15;

  canActivate(context: EffectContext): boolean {
    return context.phase === 'defense' && context.combatInfo !== undefined;
  }

  execute(context: EffectContext): EffectResult {
    if (!context.combatInfo) return this.failure();

    return this.success([
      {
        type: 'CHANGE_ATK',
        targetId: context.combatInfo.attackerId,
        value: -1,
      },
    ]);
  }
}

/**
 * Dismantle Effect (分解)
 * Target robot goes to Forest instead of Graveyard.
 */
export class DismantleEffect extends BaseEffect {
  readonly type = 'dismantle' as const;
  readonly trigger = 'onCombatResolve' as const;
  readonly priority = 30;

  canActivate(context: EffectContext): boolean {
    return context.combatInfo !== undefined;
  }

  execute(context: EffectContext): EffectResult {
    if (!context.combatInfo) return this.failure();

    return this.success([
      {
        type: 'ADD_EFFECT',
        targetId: context.combatInfo.defenderId,
        value: { dismantled: true, destination: 'forest' },
      },
    ]);
  }
}

/**
 * Mecha Merge Effect (機械合體)
 * Can merge with another robot unit.
 */
export class MechaMergeEffect extends BaseEffect {
  readonly type = 'mechaMerge' as const;
  readonly trigger = 'onSummon' as const;
  readonly priority = 10;

  canActivate(): boolean {
    return true;
  }

  execute(context: EffectContext): EffectResult {
    return this.success([
      {
        type: 'ADD_EFFECT',
        targetId: context.sourceCard.id,
        value: { canMerge: true, mergeType: 'mecha' },
      },
    ]);
  }
}

/**
 * Devour Merge Effect (吞噬合體)
 * Absorbs another unit to gain ATK.
 */
export class DevourMergeEffect extends BaseEffect {
  readonly type = 'devourMerge' as const;
  readonly trigger = 'onSummon' as const;
  readonly priority = 10;

  canActivate(): boolean {
    return true;
  }

  execute(context: EffectContext): EffectResult {
    return this.success([
      {
        type: 'ADD_EFFECT',
        targetId: context.sourceCard.id,
        value: { canMerge: true, mergeType: 'devour' },
      },
    ]);
  }
}

/**
 * Parasite Merge Effect (寄生合體)
 * Attaches to enemy unit.
 */
export class ParasiteMergeEffect extends BaseEffect {
  readonly type = 'parasiteMerge' as const;
  readonly trigger = 'onSummon' as const;
  readonly priority = 10;

  canActivate(): boolean {
    return true;
  }

  execute(context: EffectContext): EffectResult {
    return this.success([
      {
        type: 'ADD_EFFECT',
        targetId: context.sourceCard.id,
        value: { canMerge: true, mergeType: 'parasite' },
      },
    ]);
  }
}

/**
 * Charge Effect (充能)
 * Gain +1 ATK when card is drawn.
 */
export class ChargeEffect extends BaseEffect {
  readonly type = 'charge' as const;
  readonly trigger = 'onSummon' as const;
  readonly priority = 20;

  canActivate(): boolean {
    return true;
  }

  execute(context: EffectContext): EffectResult {
    return this.success([
      {
        type: 'CHANGE_ATK',
        targetId: context.sourceCard.id,
        value: 1,
      },
    ]);
  }
}

/**
 * Drain Effect (吸取)
 * Steals ATK from defeated unit.
 */
export class DrainEffect extends BaseEffect {
  readonly type = 'drain' as const;
  readonly trigger = 'onCombatResolve' as const;
  readonly priority = 35;

  canActivate(context: EffectContext): boolean {
    return context.combatInfo !== undefined;
  }

  execute(context: EffectContext): EffectResult {
    if (!context.combatInfo) return this.failure();

    return this.success([
      {
        type: 'CHANGE_ATK',
        targetId: context.sourceCard.id,
        value: 1, // Drain 1 ATK
      },
    ]);
  }
}

/**
 * Magic Resist Effect (魔法抗性)
 * Immune to spell effects.
 */
export class MagicResistEffect extends BaseEffect {
  readonly type = 'magicResist' as const;
  readonly trigger = 'onTarget' as const;
  readonly priority = 1;

  canActivate(): boolean {
    return true;
  }

  execute(context: EffectContext): EffectResult {
    return this.success([
      {
        type: 'ADD_EFFECT',
        targetId: context.sourceCard.id,
        value: { magicImmune: true },
      },
    ]);
  }
}

/**
 * Destroy Resist Effect (破壞抗性)
 * Cannot be destroyed by effects.
 */
export class DestroyResistEffect extends BaseEffect {
  readonly type = 'destroyResist' as const;
  readonly trigger = 'onTarget' as const;
  readonly priority = 1;

  canActivate(): boolean {
    return true;
  }

  execute(context: EffectContext): EffectResult {
    return this.success([
      {
        type: 'ADD_EFFECT',
        targetId: context.sourceCard.id,
        value: { destroyImmune: true },
      },
    ]);
  }
}

/**
 * Bind Effect (束縛)
 * Target cannot move next turn.
 */
export class BindEffect extends BaseEffect {
  readonly type = 'bind' as const;
  readonly trigger = 'onCombatResolve' as const;
  readonly priority = 45;

  canActivate(context: EffectContext): boolean {
    return context.combatInfo !== undefined;
  }

  execute(context: EffectContext): EffectResult {
    if (!context.combatInfo) return this.failure();

    return this.success([
      {
        type: 'ADD_EFFECT',
        targetId: context.combatInfo.defenderId,
        value: { bound: true },
      },
    ]);
  }
}

/**
 * Board Effect (搭乘)
 * Can board another unit for transport.
 */
export class BoardEffect extends BaseEffect {
  readonly type = 'board' as const;
  readonly trigger = 'onMove' as const;
  readonly priority = 10;

  canActivate(): boolean {
    return true;
  }

  execute(context: EffectContext): EffectResult {
    return this.success([
      {
        type: 'ADD_EFFECT',
        targetId: context.sourceCard.id,
        value: { canBoard: true },
      },
    ]);
  }
}

/**
 * Revive Effect (復活)
 * Can return from graveyard.
 */
export class ReviveEffect extends BaseEffect {
  readonly type = 'revive' as const;
  readonly trigger = 'onPhaseEnd' as const;
  readonly priority = 60;

  canActivate(context: EffectContext): boolean {
    return context.sourceCard.location === 'graveyard';
  }

  execute(context: EffectContext): EffectResult {
    return this.success([
      {
        type: 'MOVE_CARD',
        targetId: context.sourceCard.id,
        value: { to: 'battlefield' },
      },
    ]);
  }
}

/**
 * Transform Effect (變形)
 * Changes into another unit.
 */
export class TransformEffect extends BaseEffect {
  readonly type = 'transform' as const;
  readonly trigger = 'onSummon' as const;
  readonly priority = 5;

  canActivate(): boolean {
    return true;
  }

  execute(context: EffectContext): EffectResult {
    return this.success([
      {
        type: 'ADD_EFFECT',
        targetId: context.sourceCard.id,
        value: { canTransform: true },
      },
    ]);
  }
}

/**
 * Decay Effect (衰退)
 * ATK decreases by 1 at end of each turn.
 * When ATK reaches 0, unit is destroyed.
 */
export class DecayEffect extends BaseEffect {
  readonly type = 'decay' as const;
  readonly trigger = 'onTurnEnd' as const;
  readonly priority = 70;

  canActivate(context: EffectContext): boolean {
    // Only activate for units on battlefield
    return context.sourceCard.location === 'battlefield';
  }

  execute(context: EffectContext): EffectResult {
    return this.success([
      {
        type: 'CHANGE_ATK',
        targetId: context.sourceCard.id,
        value: -1,
      },
      {
        type: 'ADD_EFFECT',
        targetId: context.sourceCard.id,
        value: { decaying: true, checkDestroy: true },
      },
    ]);
  }
}

/**
 * Vanish Effect (消逝)
 * Spell is removed from game after use (not sent to graveyard).
 */
export class VanishEffect extends BaseEffect {
  readonly type = 'vanish' as const;
  readonly trigger = 'onSpellCast' as const;
  readonly priority = 100;

  canActivate(context: EffectContext): boolean {
    // Only for spell cards
    return context.sourceCard.location === 'hand';
  }

  execute(context: EffectContext): EffectResult {
    return this.success([
      {
        type: 'ADD_EFFECT',
        targetId: context.sourceCard.id,
        value: { vanishing: true, removeFromGame: true },
      },
    ]);
  }
}

/**
 * Armor Break Effect (破甲)
 * Ignores defender's score when calculating combat.
 */
export class ArmorBreakEffect extends BaseEffect {
  readonly type = 'armorBreak' as const;
  readonly trigger = 'onAttack' as const;
  readonly priority = 15;

  canActivate(context: EffectContext): boolean {
    return context.combatInfo !== undefined;
  }

  execute(context: EffectContext): EffectResult {
    if (!context.combatInfo) return this.failure();

    return this.success([
      {
        type: 'ADD_EFFECT',
        targetId: context.sourceCard.id,
        value: { armorBreaking: true },
      },
    ]);
  }
}

/**
 * Taunt Effect (嘲諷)
 * Forces enemy attacks to target this unit first.
 */
export class TauntEffect extends BaseEffect {
  readonly type = 'taunt' as const;
  readonly trigger = 'passive' as const;
  readonly priority = 5;

  canActivate(context: EffectContext): boolean {
    return context.sourceCard.location === 'battlefield';
  }

  execute(context: EffectContext): EffectResult {
    return this.success([
      {
        type: 'ADD_EFFECT',
        targetId: context.sourceCard.id,
        value: { taunting: true, mustBeAttacked: true },
      },
    ]);
  }
}

/**
 * Stealth Effect (隱匿)
 * Cannot be targeted by enemy effects.
 */
export class StealthEffect extends BaseEffect {
  readonly type = 'stealth' as const;
  readonly trigger = 'passive' as const;
  readonly priority = 5;

  canActivate(context: EffectContext): boolean {
    return context.sourceCard.location === 'battlefield';
  }

  execute(context: EffectContext): EffectResult {
    return this.success([
      {
        type: 'ADD_EFFECT',
        targetId: context.sourceCard.id,
        value: { stealthed: true, cannotBeTargeted: true },
      },
    ]);
  }
}

/**
 * Flying Effect (飛行)
 * Can be placed anywhere on the battlefield, ignoring normal placement rules.
 */
export class FlyingEffect extends BaseEffect {
  readonly type = 'flying' as const;
  readonly trigger = 'onSummon' as const;
  readonly priority = 5;

  canActivate(): boolean {
    return true;
  }

  execute(context: EffectContext): EffectResult {
    return this.success([
      {
        type: 'ADD_EFFECT',
        targetId: context.sourceCard.id,
        value: { flying: true, canPlaceAnywhere: true },
      },
    ]);
  }
}
